import { Checkbox, SvgIcon } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Colors } from 'copyright-clearance-center-common-ui';
import { FormItemBase, IFormItemBaseProps } from 'copyright-clearance-center-common-ui-react';

const useStyles = makeStyles(() =>
	createStyles({
		element: {
			cursor: 'pointer'
		},
		disabledElement: {
			cursor: 'not-allowed',
			color: 'rgba(0, 0, 0, 0.26)'
		}
	})
);

export interface ICheckBoxProps extends IFormItemBaseProps {
	testId?: string;
	checked: boolean;
	onChange?: (checked: boolean) => void;
	label?: string;
	colorLabel?: boolean;
	indeterminate?: boolean;
}

const CheckBox: React.FunctionComponent<ICheckBoxProps> = props => {
	const classes = useStyles();

	const uncheckedCheckboxIcon = () => {
		return (
			<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M4 4v16h16V4H4zM3 2a1 1 0 00-1 1v18a1 1 0 001 1h18a1 1 0 001-1V3a1 1 0 00-1-1H3z"
				/>
			</svg>
		);
	};

	const checkedCheckboxIcon = () => {
		return (
			<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M3 2a1 1 0 00-1 1v18a1 1 0 001 1h18a1 1 0 001-1V3a1 1 0 00-1-1H3zm15.36 8.466a2 2 0 00-2.72-2.932l-5.476 5.084-1.645-1.92a2 2 0 00-3.038 2.604l3 3.5a2 2 0 002.88.164l7-6.5z"
				/>
			</svg>
		);
	};

	const indeterminateCheckboxIcon = () => {
		return (
			<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M3 2a1 1 0 00-1 1v18a1 1 0 001 1h18a1 1 0 001-1V3a1 1 0 00-1-1H3zm6 8c-.552 0-2 .5-2 2s1.448 2 2 2h6c.552 0 2-.5 2-2s-1.448-2-2-2H9z"
				/>
			</svg>
		);
	};

	const checkboxProperties = {
		inputProps: { 'data-test-id': props.testId } as React.InputHTMLAttributes<HTMLInputElement>,
		disableRipple: true,
		checkedIcon: <SvgIcon component={checkedCheckboxIcon}></SvgIcon>,
		indeterminateIcon: <SvgIcon component={indeterminateCheckboxIcon}></SvgIcon>,
		icon: <SvgIcon component={uncheckedCheckboxIcon}></SvgIcon>,
		checked: props.checked,
		onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
			if (props.onChange) {
				props.onChange(event.target.checked);
			}
		},
		sx: {
			'&:hover': {
				backgroundColor: 'transparent',
				color: `${Colors.primary300} !important`
			},
			padding: props.label ? '0 0.5rem 0 0' : '0',
			color: Colors.neutral700,
			'& svg': {
				height: '1rem',
				width: '1rem',
				fill: 'currentColor'
			},
			'&.Mui-checked': {
				color: Colors.primary500
			}
		},
		indeterminate: props.indeterminate,
		id: props.id,
		disabled: props.disabled
	};

	const colorLabel = (props.checked || props.indeterminate) && props.colorLabel;
	const color = colorLabel ? Colors.primary500 : Colors.neutral900;

	if (!props.label) {
		return <Checkbox {...checkboxProperties} />;
	} else {
		return (
			<FormItemBase
				{...props}
				style={{ fontWeight: 400, fontSize: '0.875rem', marginRight: '1rem', color: color, ...props.style }}
				label=""
			>
				<label
					style={{ display: 'flex' }}
					className={clsx({ [classes.disabledElement]: props.disabled, [classes.element]: !props.disabled })}
					htmlFor={props.id}
				>
					<Checkbox {...checkboxProperties} />
					{props.label}
				</label>
			</FormItemBase>
		);
	}
};

export default CheckBox;
